import { Intent } from '../ui';
import React, { useEffect, useRef, useState } from 'react';
import { discardError as discardErrorState, useErrors } from '../../state/app-state';
import { AppToaster } from '../../utils/toaster';
import { accessDeniedErrorIdentifier } from './error-identifiers/access-denied-error-identifier';
import { cancelSubsBeforeDeactivationErrorIdentifier } from './error-identifiers/cancel-subs-before-deactivation-error-identifier';
import { emailAlreadyTakenErrorIdentifier } from './error-identifiers/email-already-taken-error-identifier';
import { emailNotConnectedErrorIdentifier } from './error-identifiers/email-not-connected-error-identifier';
import { emailUnsubscribeErrorIdentifier } from './error-identifiers/email-unsubscribe-error-identifier';
import { ErrorIdentifier } from './error-identifiers/error-identifier';
import { genericGraphQLErrorIdentifier } from './error-identifiers/generic-graphql-error-identifier';
import { invalidCredentialsErrorIdentifier } from './error-identifiers/invalid-credentials-error-identifier';
import { invalidLoginVerificationErrorIdentifier } from './error-identifiers/invalid-login-verification-error-identifier';
import { invalidReportRecipientErrorIdentifier } from './error-identifiers/invalid-report-recipient-error-identifier';
import { invalidResetPasswordCodeErrorIdentifier } from './error-identifiers/invalid-reset-password-code-error-identifier';
import { invalidTwitterCredentialsErrorIdentifier } from './error-identifiers/invalid-twitter-credentials-error-identifier';
import { loggingTroubleErrorIdentifier } from './error-identifiers/logging-trouble-error-identifier';
import { networkErrorIdentifier } from './error-identifiers/network-error-identifier';
import { notAuthenticatedErrorIdentifier } from './error-identifiers/not-authenticated-error-identifier';
import { reportNotFoundErrorIdentifier } from './error-identifiers/report-not-found-error-identifier';
import { twitterAccountAlreadyInuseErrorIdentifier } from './error-identifiers/twitter-account-already-inuse-error-identifier';

// Currently Disabled:
// blankErrorIdentifier
const knownErrorIdentifiers: ErrorIdentifier[] = [
  networkErrorIdentifier,
  invalidCredentialsErrorIdentifier,
  emailNotConnectedErrorIdentifier,
  loggingTroubleErrorIdentifier,
  emailAlreadyTakenErrorIdentifier,
  reportNotFoundErrorIdentifier,
  accessDeniedErrorIdentifier,
  notAuthenticatedErrorIdentifier,
  invalidReportRecipientErrorIdentifier,
  invalidResetPasswordCodeErrorIdentifier,
  cancelSubsBeforeDeactivationErrorIdentifier,
  invalidTwitterCredentialsErrorIdentifier,
  invalidLoginVerificationErrorIdentifier,
  twitterAccountAlreadyInuseErrorIdentifier,
  emailUnsubscribeErrorIdentifier,
  genericGraphQLErrorIdentifier,
];

export default function ErrorReporter() {
  const errors = useErrors();

  const [errorElements, setErrorElements] = useState<Map<Error, JSX.Element>>(new Map());
  const processedErrorsRef = useRef<Set<Error>>(new Set());

  useEffect(() => {
    const currentErrors = new Set(errors);

    // Remove processed errors and stored elements that no longer exist
    processedErrorsRef.current.forEach(error => {
      if (!currentErrors.has(error)) {
        processedErrorsRef.current.delete(error);
      }
    });

    setErrorElements(prev => {
      if (prev.size === 0) {
        return prev;
      }

      const next = new Map(prev);
      prev.forEach((_, error) => {
        if (!currentErrors.has(error)) {
          next.delete(error);
        }
      });

      return next;
    });

    errors.forEach(error => {
      if (!error) {
        return;
      }

      if (processedErrorsRef.current.has(error)) {
        return;
      }

      let errorElement: JSX.Element | null | false = null;

      for (let i = 0; i < knownErrorIdentifiers.length && errorElement === null; i++) {
        errorElement = knownErrorIdentifiers[i](i, error, (error: Error) =>
          discardErrorState(error)
        );
      }

      if (errorElement === null) {
        AppToaster.show({
          intent: Intent.DANGER,
          icon: 'warning-sign',
          message:
            'Oops! Something went wrong. Please contact us at help@newslit.co if you continue having issues.',
        });

        discardErrorState(error);
        processedErrorsRef.current.add(error);
        return;
      }

      if (errorElement === false) {
        processedErrorsRef.current.add(error);
        return;
      }

      setErrorElements(prev => {
        const next = new Map(prev);
        next.set(error, errorElement as JSX.Element);
        return next;
      });

      processedErrorsRef.current.add(error);
    });
  }, [errors]);

  return <>{errors.map(error => errorElements.get(error) ?? null)}</>;
}
